import {createContext, JSX, ReactNode, useEffect, useState} from "react";
import {keycloak} from "../services/keycloak";
import {APPIMAGES} from "../utils/global-variables-and-types";

type AuthContextType = {
    isAuthenticated: boolean;
    token?: string;
    login: () => void;
    logout: () => void;
};

export const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    login: () => {
    },
    logout: () =>
        keycloak.logout({
            redirectUri: window.location.origin,
        }),
});

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({children}: AuthProviderProps): JSX.Element => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState<string | undefined>(undefined);
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const authenticated = await keycloak.init({
                    onLoad: "login-required",
                    checkLoginIframe: false,
                    redirectUri: window.location.href
                });

                setIsAuthenticated(!!authenticated);
                if (keycloak.token) {
                    setToken(keycloak.token);
                    sessionStorage.setItem("token", keycloak.token);
                }
            } catch (err) {
                console.error("Error initializing Keycloak:", err);
            } finally {
                setInitializing(false);
            }
        })();

        keycloak.onAuthSuccess = () => {
            if (keycloak.token) {
                setToken(keycloak.token);
                sessionStorage.setItem("token", keycloak.token);
            }
            console.log("✅ Auth success");
        };

        keycloak.onAuthRefreshSuccess = () => {
            if (keycloak.token) {
                setToken(keycloak.token);
                sessionStorage.setItem("token", keycloak.token);
            }
            console.log("🔄 Token automatically refreshed");
        };

        keycloak.onAuthLogout = () => {
            setIsAuthenticated(false);
            setToken(undefined);
            sessionStorage.removeItem("token");
            console.log("🚪 Logout");
        };

        // The access token has a short lifetime; try to silently renew it
        // before it expires instead of letting API calls start failing.
        keycloak.onTokenExpired = () => {
            keycloak.updateToken(30).catch(() => {
                sessionStorage.removeItem("token");
                keycloak.logout({redirectUri: window.location.origin});
            });
        };

        // If the refresh token itself is no longer valid, there's no way to
        // recover silently — send the user back to the login screen.
        keycloak.onAuthRefreshError = () => {
            sessionStorage.removeItem("token");
            keycloak.logout({redirectUri: window.location.origin});
        };
    }, []);

    if (initializing) {
        return (
            <div className="w-screen h-screen flex items-center justify-center bg-bg">
                <img className="size-16 animate-pulse" src={APPIMAGES.logo} alt="FormsAware"/>
            </div>
        );
    }

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                token,
                login: () => keycloak.login(),
                logout: () =>
                    keycloak.logout({
                        redirectUri: window.location.origin,
                    }),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
